import useSWR from 'swr';
import { useState, useEffect, useMemo } from 'react';
import api from './axios';
import { useAuth } from '../auth/AuthContext';
import { Product } from '../types/product';
import axios from './axios';

// Fetcher without authentication
const fetcherWOauth = (url: string) => api.get(url).then(res => res.data);

// SWR options
const options = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  errorRetryCount: 3,
};

export function useGetProductsByCollection(collectionId: number) {
  const auth = useAuth();
  const URL = `/api/products?collectionId=${collectionId}&cache=${auth.user?.id || 'anonymous'}`;

  const { data, isLoading, error } = useSWR(URL, fetcherWOauth, options);
  const [productsData, setProductsData] = useState<Product[]>([]);

  useEffect(() => {
    if (!data) return;

    setProductsData(data);
  }, [data]);

  const memoizedValue = useMemo(() => {
    return {
      productsData,
      productsIsLoading: isLoading,
      error,
    };
  }, [productsData, isLoading, error]);

  return memoizedValue;
}

export function useGetOurPickProducts() {
  const auth = useAuth();
  const URL = `/api/shop/products?ourPick=true&cache=${auth.user?.id || 'anonymous'}`;

  const { data, isLoading, error } = useSWR<PaginatedProductsResponse>(URL, fetcherWOauth, options);
  const [productsData, setProductsData] = useState<Product[]>([]);

  useEffect(() => {
    if (!data) return;

    // Handle the paginated response structure
    if (data.products && Array.isArray(data.products)) {
      setProductsData(data.products);
    } else if (Array.isArray(data)) {
      // Fallback for direct array response
      setProductsData(data);
    } else {
      setProductsData([]);
    }
  }, [data]);

  const memoizedValue = useMemo(() => {
    return {
      productsData,
      productsIsLoading: isLoading,
      error,
    };
  }, [productsData, isLoading, error]);

  return memoizedValue;
}

export function useGetOurPickProductsPaginated(page: number = 1, limit: number = 12) {
  const auth = useAuth();
  const URL = `/api/shop/products?ourPick=true&page=${page}&limit=${limit}&cache=${auth.user?.id || 'anonymous'}`;

  const { data, isLoading, error, mutate } = useSWR<PaginatedProductsResponse>(URL, fetcherWOauth, options);

  const memoizedValue = useMemo(() => {
    return {
      productsData: data?.products || [],
      totalPages: data?.totalPages || 1,
      totalItems: data?.total || 0,
      currentPage: data?.page || 1,
      productsIsLoading: isLoading,
      error,
      mutate,
    };
  }, [data, isLoading, error, mutate]);

  return memoizedValue;
}

// Legacy API object for backward compatibility
export const productApi = {
  async getProductsByCollection(collectionId: number): Promise<Product[]> {
    const response = await api.get<Product[]>(`/api/products?collectionId=${collectionId}`);
    return response.data;
  },
  
  async getProductById(productId: number): Promise<Product> {
    const response = await api.get<Product>(`/api/products/${productId}`);
    return response.data;
  },
};

// Keep the old function for backward compatibility
export const getProductsByCollection = async (collectionId: number): Promise<Product[]> => {
  return productApi.getProductsByCollection(collectionId);
};

export const getMarketShippingMethods = async () => {
  const response = await axios.get('/api/market-shipping-methods');
  return response.data;
};

export type ShippingMethod = {
  id: number;
  marketId: number;
  name: string;
  type: string;
  price: number;
  minAmount: number | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PaginatedProductsResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export function useMarketShippingMethods() {
  const URL = '/api/market-shipping-methods';
  const { data, isLoading, error } = useSWR(URL, fetcherWOauth, options);
  const [shippingMethodsData, setShippingMethodsData] = useState<ShippingMethod[]>([]);

  useEffect(() => {
    if (!data) return;
    setShippingMethodsData(data);
  }, [data]);

  const memoizedValue = useMemo(() => {
    return {
      shippingMethodsData,
      shippingMethodsIsLoading: isLoading,
      error,
    };
  }, [shippingMethodsData, isLoading, error]);

  return memoizedValue;
}

export type Market = {
  id: number;
  name: string;
  countries: string[];
  createdAt: string;
  updatedAt: string;
  countriesWithNames: { code: string; name: string }[];
};

export function useMarkets() {
  const URL = '/api/markets';
  const { data, isLoading, error } = useSWR(URL, fetcherWOauth, options);
  const [marketsData, setMarketsData] = useState<Market[]>([]);

  useEffect(() => {
    if (!data) return;
    setMarketsData(data);
  }, [data]);

  const memoizedValue = useMemo(() => {
    return {
      marketsData,
      marketsIsLoading: isLoading,
      error,
    };
  }, [marketsData, isLoading, error]);

  return memoizedValue;
} 